'use client';

import type { ElementType } from 'react';
import React from 'react';
import type { Nullable } from 'corgi-types';

import appleTouchIcon from 'common/assets/icons/favicon/apple-touch-icon.png';
import favicon from 'common/assets/icons/favicon/favicon.ico';
import msMediumTile from 'common/assets/icons/favicon/ms-medium-tile.png';
import safariPinnedTab from 'common/assets/icons/favicon/safari-pinned-tab.svg';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { GSV_ID } from 'common/constants/GoogleSiteVerification';
import { useFeatureFlagValue } from 'common/hooks/feature-flags';
import { getThemeColor } from 'common/theme/helper';
import { RECAPTCHA_SITE_KEY_SCRIPT_TYPE } from 'common/utils/grecaptcha';
import { TRACE_ID_SCRIPT_TYPE } from 'common/utils/newrelic';

const DOMAIN_ANDROID_LINK = 'android-app://com.home24.android/home24/de/n';
const DOMAIN_IOS_LINK = 'ios-app://950000769/home24/de/n';
export const DEFAULT_META_VALUE = 'home24';

const getAssetSrc = (asset: string | { src?: string }) =>
  typeof asset === 'string' ? asset : asset.src;

const getAndroidHref = queryParams => `${DOMAIN_ANDROID_LINK}?${queryParams}`;
const getIOSHref = queryParams => `${DOMAIN_IOS_LINK}?${queryParams}`;

const defaultOpenGraph = {
  type: 'website',
  // This one is the same for all appdomains
  app_id: '847122918659813',
  custom: [],
  site_name: 'home24',
};

const defaultNativeAppIds = {
  iOS: '950000769',
};

interface HrefLangProps {
  url?: string;
  lang?: string;
}

interface CustomProps {
  property?: string;
  content?: string;
}

export interface GlobalHeadProps {
  /** Base url */
  baseUrl?: string;
  /** Page title */
  title?: Nullable<string>;
  /** Page description */
  description?: string;
  /** Whether to render the description meta tag */
  shouldRenderDescription?: boolean;
  /** Robots for search engine crawlers */
  robots?: string;
  /** Open Graph meta tags */
  og?: {
    url?: string;
    title?: string;
    description?: string;
    image?: string;
    app_id?: string;
    type?: string;
    custom?: CustomProps[];
  };
  /** Canonical URL */
  canonical?: Nullable<string>;
  /** Alternate language URLs */
  hreflang?: HrefLangProps[];
  /** Href list to create preconnect link tags for */
  preconnectLinks?: string[];
  /** Apps URL (query parameters only) */
  apps?: {
    android?: string;
    ios?: string;
  };
  /** Page type */
  pageType?: string;
  /** Previous page link */
  prevPageUrl?: string;
  /** Next page link */
  nextPageUrl?: string;
  /** Manifest URL */
  manifestUrl?: string;
  /** NewRelic TraceID passed from PageApps to be included in GQL calls */
  traceId?: string;
  /** ReCaptcha Site key passed from PageApps */
  reCaptchaSiteKey?: string;
  /** Wrapper component required for Next.js  */
  wrapper?: ElementType;
  /** Locale props */
  l10n: {
    language: string;
    region: string;
    theme: string;
  };
}

// eslint-disable-next-line complexity
export const BaseGlobalHead = ({
  baseUrl = 'www.home24.de',
  title = DEFAULT_META_VALUE,
  description = DEFAULT_META_VALUE,
  shouldRenderDescription = true,
  robots = 'noindex,follow',
  canonical,
  hreflang = [],
  preconnectLinks = [],
  apps,
  prevPageUrl,
  nextPageUrl,
  manifestUrl = `${process.env.CORGI_WEB_PUBLIC_PATH ?? '/corgi'}/manifest.json`,
  // pageType,
  l10n,
  traceId,
  reCaptchaSiteKey,
  wrapper,
  ...props
}: GlobalHeadProps) => {
  const { language, region, theme } = l10n;
  const { android: queryParamsAndroid = '', ios: queryParamsIOS = '' } = apps || {};

  const og = Object.assign({}, defaultOpenGraph, props.og);
  const brandColor = getThemeColor('sys.primary.background.default')(props);

  const withNativeAppDownloadLinks =
    useFeatureFlagValue(GrowthbookFlag.NativeAppDownloadMeta, 'skip') === 'include';

  const Wrapper = wrapper ?? React.Fragment;
  const appleTouchIconHref = getAssetSrc(appleTouchIcon);
  const faviconHref = getAssetSrc(favicon);
  const msMediumTileHref = getAssetSrc(msMediumTile);
  const safariPinnedTabHref = getAssetSrc(safariPinnedTab);

  return (
    <Wrapper>
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      <title>{title}</title>
      {shouldRenderDescription && <meta name="description" content={description} />}

      <base href={baseUrl} />
      <meta name="base-url" content={baseUrl} />

      <meta name="robots" content={robots} />
      <meta name="format-detection" content="telephone=no" />

      <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIconHref} />
      <link rel="manifest" href={manifestUrl} />
      {withNativeAppDownloadLinks && (
        <meta
          name="apple-itunes-app"
          content={`app-id=${defaultNativeAppIds.iOS}, app-argument=${og.url || canonical}`}
        />
      )}

      <link rel="mask-icon" color={brandColor} href={safariPinnedTabHref} />
      <link rel="icon" href={faviconHref} />

      {/*
       * Disabling the AMP version for the HomePage release with COLA.
       * Ticket: https://home24.atlassian.net/browse/FP-99
       */}
      {/* Optional amphtml tag */}
      {/* {region === 'DE' &&
        theme === 'h24' &&
        pageType === 'homepage' && (
          <link rel="amphtml" href="https://www.home24.de/amp/" />
        )} */}

      <meta name="msapplication-TileColor" content={brandColor} />
      <meta name="msapplication-square150x150logo" content={msMediumTileHref} />
      <meta name="msapplication-config" content="none" />

      {/* Required opengraph tags */}
      {(og.url || canonical) && <meta property="og:url" content={og.url || canonical} />}
      <meta property="og:title" content={og.title || title} />
      <meta property="og:description" content={og.description || description} />
      <meta property="fb:app_id" content={og.app_id} />
      <meta property="og:type" content={og.type} />
      <meta property="og:site_name" content={og.site_name} />
      <meta property="og:locale" content={`${language}_${region.toUpperCase()}`} />

      {/* Optional opengraph tags */}
      {og.image && <meta property="og:image" content={og.image} />}

      {/* Custom opengraph tags */}
      {og.custom.map((tag, index) => (
        <meta key={index} property={tag.property} content={tag.content} />
      ))}

      {preconnectLinks.map(href => (
        <link key={href} rel="preconnect" href={href} />
      ))}
      {canonical && <link href={canonical} rel="canonical" />}
      {prevPageUrl && <link href={prevPageUrl} rel="prev" />}
      {nextPageUrl && <link href={nextPageUrl} rel="next" />}

      {hreflang.map(({ url, lang }, index) => (
        <link key={index} rel="alternate" href={url} hrefLang={lang} />
      ))}

      {queryParamsAndroid && <link rel="alternate" href={getAndroidHref(queryParamsAndroid)} />}
      {queryParamsIOS && <link rel="alternate" href={getIOSHref(queryParamsIOS)} />}

      {traceId && <script type={TRACE_ID_SCRIPT_TYPE}>{traceId}</script>}

      {reCaptchaSiteKey && (
        <script type={RECAPTCHA_SITE_KEY_SCRIPT_TYPE}>{reCaptchaSiteKey}</script>
      )}

      <meta name="google-site-verification" content={GSV_ID[theme][region]} />
    </Wrapper>
  );
};
